export interface GuestTableSession {
  guest_token: string
  guest_id: string
  username: string
  table_id: string
  table_name: string
  access_id: string
  role: 'player' | 'spectator'
  expires_at: number
}

const SESSION_KEY = 'nhex.guest.tableSessions.v1'
const PENDING_INVITE_KEY = 'nhex.auth.pendingInvite.v1'
const PENDING_INVITE_TTL_MS = 24 * 60 * 60 * 1000

function readSessions(): Record<string, GuestTableSession> {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}') as Record<string, GuestTableSession>
    const now = Date.now()
    for (const [key, session] of Object.entries(parsed)) {
      if (!session?.guest_token || session.expires_at * 1000 <= now) delete parsed[key]
    }
    return parsed
  } catch {
    return {}
  }
}

function writeSessions(sessions: Record<string, GuestTableSession>): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessions))
  } catch {
    /* sessionStorage may be disabled by browser privacy settings */
  }
}

export function saveGuestSession(session: GuestTableSession): void {
  const sessions = readSessions()
  sessions[session.access_id] = session
  sessions[session.table_id] = session
  writeSessions(sessions)
}

export function getGuestSession(accessId: string): GuestTableSession | null {
  if (!accessId) return null
  const sessions = readSessions()
  writeSessions(sessions)
  return sessions[accessId] || null
}

export function clearGuestSession(session: GuestTableSession): void {
  const sessions = readSessions()
  delete sessions[session.access_id]
  delete sessions[session.table_id]
  writeSessions(sessions)
}

export function setPendingInvite(path: string): void {
  if (!path.startsWith('/invite/')) return
  try {
    localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({ path, createdAt: Date.now() }))
  } catch { /* unavailable */ }
}

export function getPendingInvite(): string {
  try {
    const raw = localStorage.getItem(PENDING_INVITE_KEY)
    if (!raw) return ''
    const value = JSON.parse(raw) as { path?: unknown; createdAt?: unknown }
    if (
      typeof value.path !== 'string'
      || !value.path.startsWith('/invite/')
      || typeof value.createdAt !== 'number'
      || Date.now() - value.createdAt > PENDING_INVITE_TTL_MS
    ) {
      localStorage.removeItem(PENDING_INVITE_KEY)
      return ''
    }
    return value.path
  } catch {
    return ''
  }
}

export function clearPendingInvite(): void {
  try { localStorage.removeItem(PENDING_INVITE_KEY) } catch { /* unavailable */ }
}
